import type { CardsBlock } from '../spec/types'

/**
 * A row of queues, each with a count, an SLA posture and a way in.
 *
 * Verbatim from `docs/design/reference/blocks/cards.html`. This is the block
 * that makes the dashboard a work surface rather than a metrics wall: a card
 * states how many items are waiting, how that stands against its target, and
 * clicks through to the screen where an operator clears them. A count with no
 * destination is a poster; a count with one is a shift.
 *
 * Two details are load-bearing:
 *
 *   - The tone is carried twice — as `data-edge` on the card (a 2px rule along
 *     its top) and as `data-tone` on the posture chip. Severity has to survive
 *     a glance across six cards at arm's length, which a chip alone does not.
 *   - The sub line reserves `min-height:2.7em` whether or not it fills. Cards
 *     of unequal height turn a scannable row into a ragged one, and the row is
 *     the point.
 *
 * No amount is formatted here. Cards carry counts, rates and postures, never
 * ledger money — where a card's unit is an ISO code the corpus supplies the
 * figure already rendered, with the code in its own adjacent span (invariant 4).
 */

interface Props {
  readonly block: CardsBlock
  readonly onNavigate: (screenId: string) => void
}

export function Cards({ block, onNavigate }: Props) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(232px,1fr))',
        gap: '1px',
        background: 'var(--k-line)',
        border: '1px solid var(--k-line)',
      }}
    >
      {block.items.map((c) => {
        const screenId = c[7]
        // The prototype gives a destination-less card a no-op handler, which
        // leaves a control that answers to nothing — a dead tab stop that
        // still lights up under the cursor. Here such a card is inert: the
        // headline metrics on Reporting and Support are readouts, not queues.
        const navigable = screenId !== ''
        return (
          <button
            key={c[0]}
            type="button"
            disabled={!navigable}
            onClick={
              navigable
                ? () => {
                    onNavigate(screenId)
                  }
                : undefined
            }
            data-edge={c[4]}
            className={navigable ? 'k-hov' : undefined}
            style={{
              display: 'block',
              textAlign: 'left',
              border: 0,
              background: 'var(--k-panel)',
              padding: '11px 12px 12px',
              cursor: navigable ? 'pointer' : 'default',
              font: 'inherit',
              color: 'var(--k-ink)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span
                style={{
                  fontSize: '11px',
                  letterSpacing: '.04em',
                  textTransform: 'uppercase',
                  color: 'var(--k-mut)',
                  flex: 1,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {c[0]}
              </span>
              <span
                data-tone={c[4]}
                style={{
                  padding: '0 4px',
                  fontSize: '9.5px',
                  fontWeight: 800,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                }}
              >
                {c[3]}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '8px' }}>
              <span
                style={{
                  fontFamily: 'var(--k-ui)',
                  fontWeight: 800,
                  fontSize: '27px',
                  letterSpacing: '-.02em',
                  lineHeight: 1,
                }}
              >
                {c[1]}
              </span>
              <span style={{ fontSize: '11px', color: 'var(--k-mut)', fontFamily: 'var(--k-mono)' }}>
                {c[2]}
              </span>
            </div>
            <div
              style={{
                fontSize: '11.5px',
                color: 'var(--k-mut)',
                marginTop: '7px',
                lineHeight: 1.35,
                minHeight: '2.7em',
                textWrap: 'pretty',
              }}
            >
              {c[5]}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '6px',
                fontSize: '10.5px',
                color: 'var(--k-mut2)',
                fontFamily: 'var(--k-mono)',
              }}
            >
              <span>{c[6]}</span>
              <span style={{ marginLeft: 'auto' }}>→</span>
            </div>
          </button>
        )
      })}
    </div>
  )
}
